/**
 * OFF-AXIS AUDIT (docs/MODELER.md, Matt 2026-08-18: "you know how Revit has a
 * warning for if a wall is slightly off axis?"). A straight edge whose angle sits
 * BARELY off a cardinal direction (0/90/180/270°) is almost never intentional — an
 * unsnapped freehand click, an imported DXF/PDF trace a hair off true, or a grip
 * drag that missed ortho by a whisker. A genuinely diagonal wall (45°, a real
 * site-angle condition) is a deliberate design choice and must never warn — the
 * tell is the SIZE of the offset, not its mere existence: Revit's own convention
 * is a tight band just above zero, which is what `warnOffAxis` reproduces.
 *
 * Values DERIVE at read time (the drafting-record principle applied to elements
 * too) — nothing is stored; every consumer re-measures the CURRENT edge on every
 * call, so the warning can never go stale.
 */
import type { ElementId, NodeId, Path2D, Pt2 } from './types'
import { resolveElement, type ModelDoc } from './doc'

/** Exclude exactly-orthogonal edges (trig round-off), not just "close to zero". */
export const OFF_AXIS_WARN_MIN_DEG = 0.01
/** Matt's stated Revit-style band — a wall this close to square is almost never meant to be angled. */
export const OFF_AXIS_WARN_MAX_DEG = 1

export interface EdgeAngleInfo {
  /** the edge's FROM node (the EdgeRef convention: this node → the next one in the path) */
  nodeId: NodeId
  /** 0–360°, CCW from +X — the plan coordinate convention (Pt2: +X east, +Y north) */
  angleDeg: number
  /** nearest cardinal direction: 0, 90, 180, or 270 */
  nearestOrthoDeg: number
  /** |angleDeg − nearestOrthoDeg|, wrapped at the 360°/0° seam — always in [0, 45] */
  offAxisDeg: number
  /** true when offAxisDeg sits in the "barely off" warning band (never true for a genuine diagonal) */
  warnOffAxis: boolean
}

/**
 * One straight edge's angle info, or null for a degenerate (zero-length) or
 * CURVED edge — "off axis" doesn't apply the same way to an intentional arc.
 */
export function edgeAngleInfo(a: Pt2, b: Pt2, nodeId: NodeId, bulge?: number): EdgeAngleInfo | null {
  if (bulge) return null
  const dx = b[0] - a[0], dy = b[1] - a[1]
  if (Math.hypot(dx, dy) < 1e-9) return null
  let angleDeg = (Math.atan2(dy, dx) * 180) / Math.PI
  if (angleDeg < 0) angleDeg += 360
  const nearestOrthoDeg = (Math.round(angleDeg / 90) * 90) % 360
  let offAxisDeg = Math.abs(angleDeg - nearestOrthoDeg)
  if (offAxisDeg > 180) offAxisDeg = 360 - offAxisDeg
  return {
    nodeId, angleDeg, nearestOrthoDeg, offAxisDeg,
    warnOffAxis: offAxisDeg > OFF_AXIS_WARN_MIN_DEG && offAxisDeg <= OFF_AXIS_WARN_MAX_DEG,
  }
}

/** Every straight edge's angle info for a path, in edge order (curved edges omitted). */
export function pathEdgeAngles(path: Path2D): EdgeAngleInfo[] {
  const out: EdgeAngleInfo[] = []
  const count = path.closed ? path.nodes.length : path.nodes.length - 1
  for (let i = 0; i < count; i++) {
    const a = path.nodes[i], b = path.nodes[(i + 1) % path.nodes.length]
    const info = edgeAngleInfo(a.pt, b.pt, a.id, a.bulge)
    if (info) out.push(info)
  }
  return out
}

/** Convenience wrapper: an element's own path/boundary, resolved and measured live. */
export function elementEdgeAngles(model: ModelDoc, elementId: ElementId): EdgeAngleInfo[] {
  const el = resolveElement(model, elementId)
  const path = (el?.params.path ?? el?.params.boundary) as Path2D | null | undefined
  if (!path?.nodes?.length) return []
  return pathEdgeAngles(path)
}
